/**
 * useDashboardListeners Hook
 * 
 * React hook for managing dashboard listeners in SparksDash page.
 * Subscribes to chat metadata and message events to keep dashboard data fresh.
 * Triggers silent refetch of chat list when events occur (no toast notifications).
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import listenerManager from './index';
import QUERYKEYS from '../../api/queryKeys';

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 500;

/**
 * Hook to manage dashboard listeners for SparksDash page
 * 
 * Subscribes to:
 * - Chat metadata events (EDIT_CHAT, DELETE_CHAT)
 * - Message events (ADD_MESSAGE, REGENERATE_MESSAGE)
 * 
 * On any event, triggers a debounced refetch of the chat list.
 * Lifecycle: Mount with SparksDash -> Unmount when navigating away
 */
export const useDashboardListeners = () => {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef(null);
  const isSubscribedRef = useRef(false);

  // Debounced refetch function — invalidates React Query cache for sparks
  const debouncedRefetch = useCallback(() => {
    // Clear any pending refetch
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Schedule new refetch
    debounceTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHATS] });
    }, DEBOUNCE_DELAY);
  }, [queryClient]);

  useEffect(() => {
    let cancelled = false;

    // Wait for ListenerManager to finish initializing before subscribing.
    // Uses the actual in-flight initialize() promise (via whenInitialized())
    // instead of a fixed-timeout poll — see whenInitialized()'s doc comment
    // in index.js for the race condition this used to have.
    listenerManager.whenInitialized().then(() => {
      if (cancelled || isSubscribedRef.current) return;
      listenerManager.subscribeDashboard(debouncedRefetch);
      isSubscribedRef.current = true;
    });

    // Cleanup on unmount
    return () => {
      cancelled = true;

      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Unsubscribe from dashboard listeners
      if (isSubscribedRef.current) {
        listenerManager.unsubscribeDashboard();
        isSubscribedRef.current = false;
      }
    };
  }, [debouncedRefetch]);

  return null;
};

export default useDashboardListeners;
